import React, { useRef, useState } from "react";
import cx from "classnames";
import { css } from "@linaria/core";

import useHeaderHeight, {
  BANNER_HEIGHT_CSS_VARIABLE,
  FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "@ui/Header/useHeaderHeight";
import SuspenseSidePanel from "./SuspenseSidePanel";
import { colors, spacings } from "@ui/Theme";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import ButtonIconV2 from "@ui/ButtonIconV2";
import Text from "@ui/Text";
import { CloseLine } from "@ui/IconV2/react";
import t from "../../../../../i18n";

export type SidePanelView = "general-information" | "formulae";

const SidePanel = ({
  title,
  subtitle,
  closeHandler,
  children,
  isFolded,
  focusedView,
  hasFormula,
  formulaCount,
}: {
  title: string;
  subtitle?: string | null;
  closeHandler: () => void;
  children: (tab: "general-information" | "formulae") => React.ReactNode;
  isFolded: boolean;
  focusedView?: SidePanelView;
  hasFormula: boolean;
  formulaCount?: number;
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<SidePanelView>(focusedView || "general-information");
  useHeaderHeight(headerRef, FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE);

  return (
    <aside className={cx(asideContainer, { isFolded })}>
      <header ref={headerRef} className={headerContainer}>
        <div className={titleBlock}>
          <Text type="h4">{title}</Text>
          {subtitle ? (
            <Text type="body1" className={titleSubtitle}>
              {subtitle}
            </Text>
          ) : null}
        </div>
        <ButtonIconV2 onClick={closeHandler}>
          <CloseLine size="s" color={colors.carbon800} />
        </ButtonIconV2>
        <NavV2>
          <Tab
            id="general-information"
            className={cx({ active: tab === "general-information" })}
            onClick={() => setTab("general-information")}
          >
            {t("project.settings.quantity.side_panel.tab.general_information")}
          </Tab>
          {hasFormula && (
            <Tab
              id="formula"
              className={cx({ active: tab === "formulae" })}
              onClick={() => setTab("formulae")}
            >
              {t("project.settings.quantity.side_panel.tab.formulae")}
              {typeof formulaCount === "number" ? ` (${formulaCount})` : null}
            </Tab>
          )}
        </NavV2>
      </header>
      <SuspenseSidePanel>{children(tab)}</SuspenseSidePanel>
    </aside>
  );
};

export default SidePanel;

//#region CSS
const asideContainer = css`
  z-index: 6;
  position: sticky;
  top: calc(var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px));
  height: calc(
    100vh - var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) - var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px)
  );

  background-color: ${colors.hydrogen};

  width: var(--side-bar-width);
  border-left: 1px solid ${colors.titanium200};
  will-change: transform;
  transition: transform var(--section-column-transition-duration)
    var(--section-column-transition-ease);

  overflow-y: auto;
  overscroll-behavior: contain;

  &.isFolded {
    transform: translateX(100%);
  }

  display: flex;
  flex-direction: column;
`;
const headerContainer = css`
  z-index: 2;
  position: sticky;
  top: 0;

  display: grid;
  grid-template-columns: 1fr max-content;
  grid-template-rows: repeat(2, max-content);

  background-color: ${colors.hydrogen};

  & > *:nth-child(1),
  & > *:nth-child(2) {
    align-self: center;
    margin: 16px 16px;
  }

  nav {
    border-bottom: 1px solid ${colors.titanium100};
    grid-column: 1 / -1;
    grid-row: 2/3;
  }
`;

const titleBlock = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
`;

const titleSubtitle = css`
  min-width: 0;
  overflow-wrap: anywhere;
`;
//#endregion CSS
